import { useEffect, useState } from "react";
import { copyfitSchema, type Copyfit } from "@proof/core";
import { Button, Control } from "./components";

const percentages = [
  ["minFontScale", "Minimum font size (%)"],
  ["minLeadingScale", "Minimum line spacing (%)"],
  ["minSpaceBeforeScale", "Minimum space before paragraph (%)"],
  ["minSpaceAfterScale", "Minimum space after paragraph (%)"],
] as const;
export function CopyfitControls({
  value,
  onChange,
}: {
  value?: Copyfit;
  onChange: (settings: Copyfit | undefined) => void;
}) {
  const encoded = JSON.stringify(value),
    initial = () => ({
      ...(Object.fromEntries(
        percentages.map(([key]) => [key, String((value?.[key] ?? 0.75) * 100)]),
      ) as Record<(typeof percentages)[number][0], string>),
      maxLinesPerParagraph: String(value?.maxLinesPerParagraph ?? ""),
      uniformGroup: value?.uniformGroup || "",
    });
  const [draft, setDraft] = useState(initial),
    [error, setError] = useState("");
  useEffect(() => {
    setDraft(initial());
    setError("");
  }, [encoded]);
  if (!value)
    return (
      <div className="inspector-note">
        <p>
          Set minimum sizes, paragraph line limits and shared reductions for
          text frames.
        </p>
        <Button
          onClick={() =>
            onChange({
              minFontScale: 0.75,
              minLeadingScale: 0.75,
              minSpaceBeforeScale: 0.75,
              minSpaceAfterScale: 0.75,
            })
          }
        >
          Add copyfit limits
        </Button>
      </div>
    );
  const apply = () => {
    const parsed = copyfitSchema.safeParse({
      ...Object.fromEntries(
        percentages.map(([key]) => [
          key,
          draft[key].trim() === "" ? NaN : Number(draft[key]) / 100,
        ]),
      ),
      maxLinesPerParagraph: draft.maxLinesPerParagraph.trim()
        ? Number(draft.maxLinesPerParagraph)
        : undefined,
      uniformGroup: draft.uniformGroup.trim() || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues.map((issue) => issue.message).join(". "));
      return;
    }
    onChange(parsed.data);
    setError("");
  };
  return (
    <details className="copyfit-controls" open>
      <summary>Copyfit limits</summary>
      <p className="inspector-note">
        Limits are percentages of the original typography. Text shrinks only as
        far as needed. At its limits, remaining overflow is reported.
      </p>
      {percentages.map(([key, label]) => (
        <Control key={key} label={label}>
          <input
            type="number"
            min={key.includes("Space") ? 0 : 1}
            max={100}
            step={1}
            value={draft[key]}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
          />
        </Control>
      ))}
      <Control label="Maximum lines per paragraph (optional)">
        <input
          type="number"
          min={1}
          max={10000}
          step={1}
          value={draft.maxLinesPerParagraph}
          onChange={(e) =>
            setDraft({ ...draft, maxLinesPerParagraph: e.target.value })
          }
        />
      </Control>
      <Control label="Uniform copyfit group (optional)">
        <input
          maxLength={100}
          value={draft.uniformGroup}
          onChange={(e) => setDraft({ ...draft, uniformGroup: e.target.value })}
        />
      </Control>
      <p className="inspector-note">
        Visible frames with the same group share one reduction, even on other
        pages. The strictest group limits apply.
      </p>
      <div className="button-row">
        <Button onClick={apply}>Apply copyfit limits</Button>
        <Button
          onClick={() => {
            setDraft(initial());
            setError("");
          }}
        >
          Reset copyfit edits
        </Button>
        <Button onClick={() => onChange(undefined)}>
          Remove copyfit limits
        </Button>
      </div>
      {error && <p role="alert">{error}</p>}
    </details>
  );
}
